/**
 * Markdown 渲染（PLAN §6.2 / R1）
 * - 基于 markdown-it
 * - 图片尺寸语法（Obsidian 风格）：`![alt|WIDTHxHEIGHT](url)` / `![alt|WIDTH](url)`
 *   无法解析的旧语法 `![alt](url)` 保持原行为
 * - sanitize：仅允许 http(s): 链接、data:image 图片白名单；原始 HTML 一律转义（防 XSS）
 */
import MarkdownIt from 'markdown-it';
/** 构建 markdown-it 实例（html=false 时原始 HTML 被转义输出） */
function createRenderer() {
    const md = new MarkdownIt({
        html: false, // 转义 raw HTML，防 XSS
        linkify: true,
        breaks: true,
    });
    // ---------- 图片：解析 [alt|WIDTHxHEIGHT] / [alt|WIDTH] ----------
    md.renderer.rules.image = (tokens, idx) => {
        const token = tokens[idx];
        const src = token.attrGet('src') ?? '';
        const alt = token.content ?? ''; // markdown-it 将 alt 存于 content
        const { alt: realAlt, size } = splitAltSize(alt);
        const width = size ? size.width : null;
        const height = size ? size.height : null;
        const safeSrc = safeUrl(src, 'image');
        if (!safeSrc)
            return ''; // 非法来源丢弃
        let html = `<img src="${escapeHtml(safeSrc)}" alt="${escapeHtml(realAlt)}"`;
        if (width)
            html += ` width="${width}"`;
        if (height)
            html += ` height="${height}"`;
        return `${html}>`;
    };
    // ---------- 链接：仅 http(s) ----------
    const linkOpen = md.renderer.rules.link_open ?? ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options));
    md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
        const token = tokens[idx];
        const href = token.attrGet('href') ?? '';
        if (href && !safeUrl(href, 'link')) {
            token.attrSet('href', '#');
            token.attrSet('class', 'unsafe-link');
        }
        return linkOpen(tokens, idx, options, env, self);
    };
    return md;
}
/** 从 alt 解析出真实 alt 与可选尺寸（`alt|300x200` / `alt|300`） */
function splitAltSize(alt) {
    const parts = alt.split('|');
    const realAlt = parts[0] ?? '';
    const sizePart = parts[1]?.trim() ?? '';
    const m = sizePart.match(/^(\d+)(?:x(\d+))?$/i);
    if (!m)
        return { alt: realAlt, size: null };
    const width = Number(m[1]);
    const height = m[2] !== undefined ? Number(m[2]) : null;
    if (!Number.isFinite(width) || (height !== null && !Number.isFinite(height))) {
        return { alt: realAlt, size: null };
    }
    return { alt: realAlt, size: { width, height } };
}
/** URL 白名单：链接仅 http(s)；图片额外允许 data:image */
function safeUrl(url, kind) {
    const u = url.trim();
    if (/^https?:\/\//i.test(u))
        return u;
    if (kind === 'image' && /^data:image\/(png|jpe?g|gif|webp|svg\+xml);base64,/i.test(u))
        return u;
    return null;
}
/** HTML 转义 */
function escapeHtml(s) {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
let cached = null;
/** 渲染 Markdown 为安全 HTML（模块级缓存实例） */
export function renderMarkdown(src) {
    const md = cached ?? (cached = createRenderer());
    return md.render(src);
}
//# sourceMappingURL=index.js.map